import React, { useState, useEffect, useContext } from 'react';
import {
  useDeepCompareEffect,
  useLocation,
  useToggle,
  useUpdateEffect,
} from 'react-use';
import { find, isEmpty } from 'lodash';
import { Container, Card, Flex, Text } from '../../design-system';
import { Block } from '../../design-system';
import { ProjectsContext, ACTIONS } from '../../contexts/projectsContext';
import { isCompleted } from '../../globalConstants';
import {
  DEADLINE_OPTIONS,
  ACTIVE_OPTIONS,
  nameFromArray,
} from '../shared-constants';
import {
  COMPLETED_COLUMNS,
  PENDING_COLUMNS,
  PROJECT_COLUMNS,
  PROJECT_SECONDARY_COLUMNS,
  RIGHT_COLUMN,
  STATUS_OPTIONS,
} from './constants';
import AddProjectModal from '../../components/AddProjectModal';
import ContainerHeader from '../../components/ContainerHeader';
import { Table } from '../../components/Table';
import TableFilter from '../../components/TableFilter';
import { useData } from '../../helpers/data-loader';
import { createSelectOptions, errorMsg } from '../../helpers/utils';
import useTableSort, { SavedSort } from '../../hooks/useTableSort';

const defaultColumns = [
  ...PROJECT_COLUMNS,
  ...PROJECT_SECONDARY_COLUMNS,
  ...RIGHT_COLUMN,
];

// Must match the $sortColumn default in ProjectController::index().
// If you change one, change the other.
const DEFAULT_PROJECTS_SORT: SavedSort = { id: 'deadline', desc: false };

const Projects = () => {
  const { initialSortBy, initialFilterParams, persistSort } = useTableSort(
    'projectsTableSort',
    DEFAULT_PROJECTS_SORT,
  );

  const [projectData, fetchProjectData, projectLoading] = useData('/project', {
    errorMsg: errorMsg('projects'),
  });
  const [funderData, fetchFunderData] = useData('/funder?simple=1', {
    errorMsg: errorMsg('funders'),
  });
  const [clientData, fetchClientData] = useData('/client?simple=1', {
    errorMsg: errorMsg('clients'),
  });
  const [userData, fetchUserData] = useData('/user', {
    errorMsg: errorMsg('users'),
  });

  const {
    state: { search, filters },
    dispatch,
  } = useContext(ProjectsContext);

  const location = useLocation();
  const [filtersOpen, toggleFilters] = useToggle(false);
  const [projectColumns, setProjectColumns] = useState(defaultColumns);
  const [filterParams, setFetchParams] = useState<any>(initialFilterParams);
  const [searchValue, setSearchValue] = useState('');
  const clientOptions = createSelectOptions(clientData);
  const funderOptions = createSelectOptions(funderData);
  const userOptions = createSelectOptions(userData);
  const { status } = filters;

  const FILTER_FIELDS = [
    {
      name: 'client',
      label: 'Client',
      component: 'select',
      options: clientOptions,
    },
    {
      name: 'funder',
      label: 'Funder',
      component: 'select',
      options: funderOptions,
    },
    {
      name: 'projectLead',
      label: 'Project Lead',
      component: 'select',
      options: userOptions,
    },
    {
      name: 'deadline',
      label: 'Deadline',
      component: 'select',
      options: DEADLINE_OPTIONS,
      sortOptions: false,
      isClearable: false,
    },
    {
      name: 'status',
      label: 'Status',
      component: 'select',
      options: STATUS_OPTIONS,
      sortOptions: false,
      isClearable: false,
    },
    {
      name: 'active',
      label: 'Active Status',
      component: 'select',
      options: ACTIVE_OPTIONS,
      sortOptions: false,
      isClearable: true,
    },
  ];

  useDeepCompareEffect(() => {
    if (isEmpty(clientData)) fetchClientData();
    if (isEmpty(funderData)) fetchFunderData();
    if (isEmpty(userData)) fetchUserData();
  }, [location]);

  useEffect(() => {
    if (isCompleted(status)) {
      setProjectColumns([
        ...PROJECT_COLUMNS,
        ...(status === 'Pending' ? PENDING_COLUMNS : COMPLETED_COLUMNS),
        ...RIGHT_COLUMN,
      ]);
      return;
    }
    setProjectColumns(defaultColumns);
  }, [status]);

  const makeReq = () => {
    fetchProjectData({
      params: { ...filterParams, ...filters, search: searchValue },
    });
  };

  useUpdateEffect(() => {
    makeReq();
  }, [filterParams, searchValue, filters]);

  const saveFilters = (values: any) => {
    dispatch({
      type: ACTIONS.SET_FILTERS,
      value: values,
    });
    toggleFilters();
  };

  const clearFilters = () => dispatch({ type: ACTIONS.CLEAR_FILTERS });

  const filtersToDisplay = React.useMemo(() => {
    return Object.entries(filters)
      .filter(([filter, value]) => {
        if (!value) {
          return false;
        }

        if (filter === 'deadline' && value === 0) {
          return false;
        }

        if (filter === 'status' && value === 'All') {
          return false;
        }

        return true;
      })
      .map(([filter, value]) => {
        let displayValue = value;

        if (filter === 'funder') {
          displayValue = nameFromArray(value, funderData);
        }

        if (filter === 'client') {
          displayValue = nameFromArray(value, clientData);
        }

        if (filter === 'projectLead') {
          displayValue = nameFromArray(value, userData);
        }

        if (filter === 'deadline') {
          const search_deadline = find(DEADLINE_OPTIONS, {
            value: Number(value),
          });
          if (search_deadline) {
            displayValue = search_deadline.label;
          } else {
            displayValue = `${value} days`;
          }
        }

        if (filter === 'active') {
          filter = 'status';
          const active_filter = find(ACTIVE_OPTIONS, {
            value,
          });
          if (active_filter) {
            displayValue = active_filter.label;
          }
        }

        return { filter, displayValue };
      });
  }, [clientData, funderData, userData, filters]);

  const handleSearch = (value: string) => {
    setSearchValue(value);
  };

  const handlePageChange = ({
    pageIndex,
    pageSize,
    sortColumn,
    sortDesc,
  }: {
    pageIndex: number;
    pageSize: number;
    sortColumn: any;
    sortDesc: any;
  }) => {
    persistSort(sortColumn, sortDesc);
    setFetchParams((prevState) => ({
      ...prevState,
      page: pageIndex + 1,
      page_size: pageSize,
      sortColumn,
      sortDesc,
    }));
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const preset = Object.fromEntries(params.entries());
    // Apply any URL-preset filters directly. Don't call saveFilters here — it
    // toggles the filter modal open, which the old extra toggleFilters() then
    // closed, causing a flicker plus a duplicate SET_FILTERS dispatch.
    dispatch({
      type: ACTIONS.SET_FILTERS,
      value: { ...filters, ...preset },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container fluid gutter={50}>
      <Card mt={30}>
        <ContainerHeader>
          <Flex alignItems="center" justifyContent="space-between" width="100%">
            <Text fontWeight={700} fontSize={35}>
              Projects
            </Text>
            <AddProjectModal funderData={funderData} clientData={clientData} />
          </Flex>
        </ContainerHeader>
        <Block mb={3}>
          <TableFilter
            filtersToDisplay={filtersToDisplay}
            search={search}
            filters={filters}
            handleSearch={handleSearch}
            saveFilters={saveFilters}
            isOpen={filtersOpen}
            filterFields={FILTER_FIELDS}
            clearFilters={clearFilters}
            toggleModal={toggleFilters}
          />
        </Block>
        <Table
          onPageChange={handlePageChange}
          columns={projectColumns}
          data={projectData}
          name="Projects"
          loading={projectLoading}
          initialSortBy={initialSortBy}
        />
      </Card>
    </Container>
  );
};

export default Projects;
